import React, { useCallback, useEffect, useState } from "react";
import DDRDashboard, { parseDDRWorkbook } from "./DDRDashboard";

/* ══════════════════════════════════════════════════════════════════════
   /affordability — always-on dashboard.

   Visiting the URL loads the workbook shipped in /public and renders the
   dashboard. No sign-in, no upload, no publish step, and nothing cached:
   every visit re-reads the file, so replacing it in /public is all that is
   needed to update what everyone sees.

   To swap the workbook: drop a new file into /public and, if the name
   differs, change WORKBOOK below.
   ══════════════════════════════════════════════════════════════════════ */
const WORKBOOK = "ddr_outlets.xlsx";
const XLSX_CDN = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";

/* XLSX is a CDN global here, matching the rest of the app — it is not an
   npm dependency, so a bundler import would fail to resolve. */
let xlsxPromise = null;
function loadXLSX() {
  if (window.XLSX) return Promise.resolve(window.XLSX);
  if (xlsxPromise) return xlsxPromise;
  xlsxPromise = new Promise((resolve, reject) => {
    const el = document.createElement("script");
    el.src = XLSX_CDN;
    el.async = true;
    el.onload = () => (window.XLSX
      ? resolve(window.XLSX)
      : reject(new Error("XLSX loaded but window.XLSX is undefined")));
    el.onerror = () => { xlsxPromise = null; reject(new Error("Could not load the XLSX library")); };
    document.head.appendChild(el);
  });
  return xlsxPromise;
}

export default function AffordabilityPage({ session = null, onAskAI }) {
  const [state, setState] = useState({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      // Cache-busted so a replaced workbook is picked up immediately
      const url = `${process.env.PUBLIC_URL || ""}/${WORKBOOK}?t=${Date.now()}`;
      const resp = await fetch(url);
      if (!resp.ok) {
        throw new Error(`Could not find /${WORKBOOK} (${resp.status}). `
          + `Place the workbook in the app's public folder.`);
      }

      const buf = await resp.arrayBuffer();
      // A dev server answers unknown paths with index.html, which would
      // otherwise fail deep inside XLSX.read with something opaque.
      const head = new TextDecoder().decode(new Uint8Array(buf.slice(0, 64)));
      if (/^\s*<!doctype|^\s*<html/i.test(head)) {
        throw new Error(`/${WORKBOOK} was not found — the server returned an HTML page. `
          + `Confirm the file sits in /public.`);
      }

      const XLSX = await loadXLSX();
      const wb = XLSX.read(buf, { type: "array" });
      const data = parseDDRWorkbook(wb, XLSX);
      if (!data) throw new Error("The workbook could not be parsed.");

      setState({ status: "ready", data });
    } catch (e) {
      setState({ status: "error", error: e.message });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (state.status === "loading") {
    return (
      <div style={wrap}>
        <div style={{ textAlign: "center" }}>
          <div style={spinner} />
          <div style={{ color: "#5C1020", fontSize: 13, fontWeight: 600, marginTop: 16 }}>
            Loading the affordability dashboard…
          </div>
          <div style={{ color: "#9A5060", fontSize: 11, marginTop: 4 }}>
            Reading {WORKBOOK}
          </div>
        </div>
        <style>{"@keyframes aff-spin{to{transform:rotate(360deg)}}"}</style>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div style={wrap}>
        <div style={{ background: "#fff", border: "1px solid #FADDDD", borderRadius: 12,
          padding: 32, maxWidth: 520, textAlign: "center",
          boxShadow: "0 2px 8px rgba(232,0,26,0.08)" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1A0005" }}>
            Could not load the dashboard
          </div>
          <div style={{ fontSize: 12, color: "#9A5060", marginTop: 8, lineHeight: 1.6 }}>
            {state.error}
          </div>
          <button onClick={load} style={{ marginTop: 18, background: "#E8001A",
            border: "none", color: "#fff", padding: "8px 18px", borderRadius: 8,
            fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Try again</button>
        </div>
      </div>
    );
  }

  return <DDRDashboard data={state.data} session={session} onAskAI={onAskAI} />;
}

const wrap = {
  minHeight: "100vh", background: "#FFF8F8", display: "flex",
  alignItems: "center", justifyContent: "center", padding: 40,
};
const spinner = {
  width: 34, height: 34, margin: "0 auto",
  border: "3px solid #FADDDD", borderTopColor: "#E8001A",
  borderRadius: "50%", animation: "aff-spin 0.8s linear infinite",
};